// What sits under the patient's name on the chart.
//
// Dr. Levitan, reading it on his phone: the id and the gender came first and
// the age was cut off by the edge of the screen — "Age then DOB I would like
// to see at first glance." So age leads, the date of birth follows it, and
// the id and gender trail, where a narrow screen may cut them and lose
// nothing he reads for. One rule for the phone header and the desktop one.

export interface PatientHeaderInput {
  dateOfBirth?: string | Date | null;
  gender?: string | null;
  internalId?: string | number | null;
}

export interface PatientHeaderFacts {
  /** "Age 73", or null when there is no usable date of birth. */
  age: string | null;
  /** "DOB 02/02/1953", or "DOB not on file". */
  dob: string;
  /** "Male" — null when missing or literally "unknown". */
  gender: string | null;
  id: string | null;
  /** The whole line, in reading order, for a header with one line of room. */
  line: string;
  /**
   * The same line for a phone header, where four action icons leave about
   * twenty characters: the "DOB" label goes, so "Age 73 · 02/02/1953" — the
   * two things the line is read for — fit before the screen cuts it.
   */
  compactLine: string;
  /**
   * compactLine in two parts, because an ellipsis costs a character and at
   * 375px that character was the last digit of the birth year. The lead —
   * age and date of birth — must never be cut; the tail is what may be.
   */
  lead: string;
  tail: string;
}

// A date of birth is a calendar date. Read the digits; never let a time zone
// move somebody's birthday.
const dateOnly = (value: PatientHeaderInput['dateOfBirth']): { y: number; m: number; d: number } | null => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : { y: value.getFullYear(), m: value.getMonth() + 1, d: value.getDate() };
  }
  const match = String(value).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return null;
  const [y, m, d] = [Number(match[1]), Number(match[2]), Number(match[3])];
  return m >= 1 && m <= 12 && d >= 1 && d <= 31 ? { y, m, d } : null;
};

const pad = (n: number) => String(n).padStart(2, '0');

export function patientHeaderFacts(patient: PatientHeaderInput, today: Date = new Date()): PatientHeaderFacts {
  const born = dateOnly(patient.dateOfBirth);

  let age: string | null = null;
  if (born) {
    let years = today.getFullYear() - born.y;
    const beforeBirthday = today.getMonth() + 1 < born.m || (today.getMonth() + 1 === born.m && today.getDate() < born.d);
    if (beforeBirthday) years -= 1;
    if (years >= 0) age = `Age ${years}`;
  }
  const dob = born ? `DOB ${pad(born.m)}/${pad(born.d)}/${born.y}` : 'DOB not on file';

  const rawGender = String(patient.gender || '').trim();
  const gender = rawGender && !/^unknown$/i.test(rawGender)
    ? rawGender.charAt(0).toUpperCase() + rawGender.slice(1)
    : null;

  const id = patient.internalId != null && String(patient.internalId).trim() ? String(patient.internalId).trim() : null;

  const dobCompact = born ? `${pad(born.m)}/${pad(born.d)}/${born.y}` : dob;

  const lead = [age, dobCompact].filter(Boolean).join(' · ');
  const tail = [gender, id].filter(Boolean).join(' · ');

  return {
    age, dob, gender, id,
    line: [age, dob, gender, id].filter(Boolean).join(' · '),
    compactLine: [lead, tail].filter(Boolean).join(' · '),
    lead,
    tail,
  };
}
